import React, { useMemo, memo, useCallback } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertTriangle,
  Plus,
  Edit,
  Trash2,
  CheckCircle2,
  XCircle,
  Copy,
  FileText,
  FolderPlus,
  GripVertical,
  Sparkles,
  Download,
} from 'lucide-react';
import { useI18n } from '@/contexts/i18n-hook';
import { traduzirClassificacao } from './constants/classificacao-risco';

interface Risco {
  id?: string;
  numero: number;
  parte: string;
  hrnAtual: number;
  classificacaoRisco?: string;
  plr?: string;
  sil?: string;
  categoriaSeguranca?: string;
  tipoPerigo?: string;
  categoria?: string;
  pronta?: boolean;
  conformeAtual?: boolean;
}

interface DadosParte {
  numero: number;
  parte: string;
  riscos: Risco[];
  pronta: boolean;
  conformeAtual: boolean;
}

interface TabelaPartesProps {
  riscos: Risco[];
  onAdicionar?: () => void;
  onAdicionarComJson?: () => void;
  onReordenar?: (novaOrdem: string[]) => void;
  onEditar?: (parte: string, riscos: Risco[]) => void;
  onDeletar?: (parte: string, riscos: Risco[]) => void;
  onDuplicar?: (parte: string, riscos: Risco[]) => void;
  onCriarTemplate?: (parte: string, riscos: Risco[]) => void;
  onAdicionarTemplateParte?: () => void;
  onDetalharIA?: (parte: string, riscos: Risco[]) => void;
  onBaixarImagens?: (parte: string, riscos: Risco[]) => void;
  modoEdicao?: boolean;
}

// Pure helper functions outside the component to avoid re-creation on render
function getClassificacaoColor(classificacao?: string): string {
  switch (classificacao?.toLowerCase()) {
    case 'inaceitável':
    case 'inaceitavel':
    case 'extremo':
    case 'muito alto':
      return 'bg-red-100 text-red-800';
    case 'alto':
      return 'bg-orange-100 text-orange-800';
    case 'significante':
      return 'bg-yellow-100 text-yellow-800';
    case 'baixo':
      return 'bg-blue-100 text-blue-800';
    case 'muito baixo':
    case 'aceitável':
    case 'aceitavel':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Parte sem nome: o texto é o identificador do agrupamento (id do drag & drop,
 * argumento de `onEditar`/`onDeletar`), então continua em português no dado e
 * só é traduzido na hora de desenhar a célula.
 */
const PARTE_SEM_NOME = 'Sem parte definida';

function getHRNClassificacao(hrn: number): string {
  if (hrn >= 1000) return 'Inaceitável';
  if (hrn >= 500) return 'Extremo';
  if (hrn >= 100) return 'Muito alto';
  if (hrn >= 50) return 'Alto';
  if (hrn >= 10) return 'Significante';
  if (hrn >= 5) return 'Baixo';
  if (hrn >= 1) return 'Muito baixo';
  return 'Aceitável';
}

// ─── Sortable row ────────────────────────────────────────────────────────────

interface SortableRowProps {
  dadosParte: DadosParte;
  modoEdicao: boolean;
  isOverlay?: boolean;
  onEditar?: (parte: string, riscos: Risco[]) => void;
  onCriarTemplate?: (parte: string, riscos: Risco[]) => void;
  onDuplicar?: (parte: string, riscos: Risco[]) => void;
  onDeletar?: (parte: string, riscos: Risco[]) => void;
  onDetalharIA?: (parte: string, riscos: Risco[]) => void;
  onBaixarImagens?: (parte: string, riscos: Risco[]) => void;
}

const SortableRow = memo(function SortableRow({
  dadosParte,
  modoEdicao,
  isOverlay = false,
  onEditar,
  onCriarTemplate,
  onDuplicar,
  onDeletar,
  onDetalharIA,
  onBaixarImagens,
}: SortableRowProps) {
  const { t } = useI18n();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dadosParte.parte,
    disabled: !modoEdicao,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const hrnMax = Math.max(...dadosParte.riscos.map((r) => r.hrnAtual || 0));
  const classificacao = getHRNClassificacao(hrnMax);
  const numeroFormatado = String(dadosParte.numero).padStart(3, '0');

  const handleRowClick = useCallback(() => {
    if (modoEdicao && onEditar) {
      onEditar(dadosParte.parte, dadosParte.riscos);
    }
  }, [modoEdicao, onEditar, dadosParte.parte, dadosParte.riscos]);

  const handleEditar = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEditar?.(dadosParte.parte, dadosParte.riscos);
    },
    [onEditar, dadosParte.parte, dadosParte.riscos]
  );

  const handleCriarTemplate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onCriarTemplate?.(dadosParte.parte, dadosParte.riscos);
    },
    [onCriarTemplate, dadosParte.parte, dadosParte.riscos]
  );

  const handleDuplicar = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDuplicar?.(dadosParte.parte, dadosParte.riscos);
    },
    [onDuplicar, dadosParte.parte, dadosParte.riscos]
  );

  const handleDetalharIA = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDetalharIA?.(dadosParte.parte, dadosParte.riscos);
    },
    [onDetalharIA, dadosParte.parte, dadosParte.riscos]
  );

  const handleBaixarImagens = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onBaixarImagens?.(dadosParte.parte, dadosParte.riscos);
    },
    [onBaixarImagens, dadosParte.parte, dadosParte.riscos]
  );

  const handleDeletar = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!onDeletar) return;
      const qty = dadosParte.riscos.length;
      const msg = t(
        qty === 1
          ? 'apreciacaoTabelas.partes.confirmarDeletar.um'
          : 'apreciacaoTabelas.partes.confirmarDeletar.varios',
        { parte: dadosParte.parte, qtd: qty }
      );
      if (confirm(msg)) {
        onDeletar(dadosParte.parte, dadosParte.riscos);
      }
    },
    [onDeletar, dadosParte.parte, dadosParte.riscos, t]
  );

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={[
        'hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark',
        modoEdicao ? 'cursor-pointer' : '',
        isDragging ? 'opacity-50 bg-blue-50 dark:bg-blue-900/20 shadow-lg' : '',
        isOverlay ? 'shadow-xl opacity-95' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleRowClick}
    >
      {/* Drag handle column */}
      {modoEdicao && (
        <td className="px-2 py-3 w-8">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 touch-none select-none"
            title={t('apreciacaoTabelas.partes.acao.arrastar')}
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </td>
      )}

      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
        {numeroFormatado}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
        <div className="font-medium">
          {dadosParte.parte === PARTE_SEM_NOME
            ? t('apreciacaoTabelas.partes.semParteDefinida')
            : dadosParte.parte}
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-center">
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getClassificacaoColor(
            classificacao
          )}`}
        >
          {hrnMax}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-center">
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getClassificacaoColor(
            classificacao
          )}`}
        >
          {traduzirClassificacao(t, classificacao)}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-center">
        {dadosParte.pronta ? (
          <span className="inline-flex items-center text-green-600 dark:text-green-400">
            <CheckCircle2 className="h-5 w-5 mr-1" />
            {t('apreciacaoTabelas.partes.status.pronta')}
          </span>
        ) : (
          <span className="inline-flex items-center text-orange-600 dark:text-orange-400">
            <XCircle className="h-5 w-5 mr-1" />
            {t('apreciacaoTabelas.partes.status.naoPronta')}
          </span>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-center">
        {dadosParte.conformeAtual ? (
          <span className="inline-flex items-center text-green-600 dark:text-green-400">
            <CheckCircle2 className="h-5 w-5 mr-1" />
            {t('apreciacaoTabelas.partes.conformidade.conforme')}
          </span>
        ) : (
          <span className="inline-flex items-center text-red-600 dark:text-red-400">
            <XCircle className="h-5 w-5 mr-1" />
            {t('apreciacaoTabelas.partes.conformidade.naoConforme')}
          </span>
        )}
      </td>
      {modoEdicao && (
        <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium">
          <div className="flex justify-center space-x-2">
            <button
              type="button"
              onClick={handleEditar}
              className="text-blue-600 dark:text-white hover:text-blue-900 dark:hover:text-schmersal-yellow"
              title={t('apreciacaoTabelas.partes.acao.editar')}
            >
              <Edit className="h-4 w-4" />
            </button>
            {onDetalharIA && (
              <button
                type="button"
                onClick={handleDetalharIA}
                className="text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300"
                title={t('apreciacaoTabelas.partes.acao.detalharIA')}
              >
                <Sparkles className="h-4 w-4" />
              </button>
            )}
            {onBaixarImagens && (
              <button
                type="button"
                onClick={handleBaixarImagens}
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                title={t('apreciacaoTabelas.partes.acao.baixarImagens')}
              >
                <Download className="h-4 w-4" />
              </button>
            )}
            {onCriarTemplate && (
              <button
                type="button"
                onClick={handleCriarTemplate}
                className="text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300"
                title={t('apreciacaoTabelas.partes.acao.criarTemplate')}
              >
                <FileText className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={handleDuplicar}
              className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
              title={t('apreciacaoTabelas.partes.acao.duplicar')}
            >
              <Copy className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleDeletar}
              className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
              title={t('apreciacaoTabelas.partes.acao.deletar')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
});

// ─── Overlay row (rendered inside DragOverlay) ───────────────────────────────

function OverlayRow({
  dadosParte,
  modoEdicao,
}: {
  dadosParte: DadosParte;
  modoEdicao: boolean;
}) {
  return (
    <SortableRow
      dadosParte={dadosParte}
      modoEdicao={modoEdicao}
      isOverlay
    />
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function TabelaPartes({
  riscos,
  onAdicionar,
  onAdicionarComJson,
  onReordenar,
  onEditar,
  onDeletar,
  onDuplicar,
  onCriarTemplate,
  onAdicionarTemplateParte,
  onDetalharIA,
  onBaixarImagens,
  modoEdicao = false,
}: TabelaPartesProps) {
  const { t } = useI18n();
  const [activeId, setActiveId] = React.useState<string | null>(null);

  // Memoised: recalculate only when riscos changes
  const partesOrdenadas = useMemo<DadosParte[]>(() => {
    const riscosOrdenados = [...riscos].sort((a, b) => (a.numero || 0) - (b.numero || 0));
    const partesUnicas: string[] = [];
    riscosOrdenados.forEach((r) => {
      const p = r.parte || PARTE_SEM_NOME;
      if (!partesUnicas.includes(p)) partesUnicas.push(p);
    });

    const map = new Map<string, DadosParte>();
    riscosOrdenados.forEach((risco) => {
      const parte = risco.parte || PARTE_SEM_NOME;
      if (!map.has(parte)) {
        map.set(parte, {
          numero: partesUnicas.indexOf(parte) + 1,
          parte,
          riscos: [],
          pronta: risco.pronta ?? false,
          conformeAtual: risco.conformeAtual ?? false,
        });
      }
      map.get(parte)!.riscos.push(risco);
    });

    return Array.from(map.values()).sort((a, b) => a.numero - b.numero);
  }, [riscos]);

  const parteIds = useMemo(() => partesOrdenadas.map((p) => p.parte), [partesOrdenadas]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const activeItem = useMemo(
    () => (activeId ? partesOrdenadas.find((p) => p.parte === activeId) ?? null : null),
    [activeId, partesOrdenadas]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = parteIds.indexOf(String(active.id));
      const newIndex = parteIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      const novaOrdem = arrayMove(parteIds, oldIndex, newIndex);
      onReordenar?.(novaOrdem);
    },
    [parteIds, onReordenar]
  );

  const handleDragCancel = useCallback(() => setActiveId(null), []);

  // Column count for colSpan (# + parte + hrnMax + classificação + status + conformidade + [drag] + [ações])
  const colSpan = modoEdicao ? 8 : 6;

  return (
    <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <AlertTriangle className="h-6 w-6 text-blue-500 dark:text-schmersal-blue mr-2" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('apreciacaoTabelas.partes.titulo')}
          </h2>
        </div>
        {modoEdicao && (
          <div className="flex items-center gap-2">
            {onAdicionarTemplateParte && (
              <button
                type="button"
                onClick={onAdicionarTemplateParte}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-white bg-white dark:bg-schmersal-gray-dark hover:bg-gray-50 dark:hover:bg-schmersal-gray"
              >
                <FolderPlus className="h-4 w-4 mr-2" />
                {t('apreciacaoTabelas.partes.acao.adicionarTemplate')}
              </button>
            )}
            {onAdicionar && (
              <button
                type="button"
                onClick={(e) => {
                  if ((e.ctrlKey || e.metaKey) && onAdicionarComJson) {
                    onAdicionarComJson();
                    return;
                  }
                  onAdicionar();
                }}
                title={t('apreciacaoTabelas.partes.acao.adicionarDica')}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 dark:bg-schmersal-blue hover:bg-blue-700 dark:hover:bg-schmersal-blue-light"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('apreciacaoTabelas.partes.acao.adicionar')}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <table className="min-w-full divide-y divide-gray-200 dark:divide-schmersal-gray-dark">
            <thead className="bg-gray-50 dark:bg-schmersal-gray-dark">
              <tr>
                {modoEdicao && (
                  <th className="w-8 px-2 py-3" aria-label={t('apreciacaoTabelas.partes.aria.reordenar')} />
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                  {t('apreciacaoTabelas.partes.coluna.parte')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                  {t('apreciacaoTabelas.partes.coluna.hrnMax')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                  {t('apreciacaoTabelas.partes.coluna.classificacao')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                  {t('comum.rotulo.status')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                  {t('apreciacaoTabelas.partes.coluna.conformidade')}
                </th>
                {modoEdicao && (
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                    {t('comum.rotulo.acoes')}
                  </th>
                )}
              </tr>
            </thead>
            <SortableContext items={parteIds} strategy={verticalListSortingStrategy}>
              <tbody className="bg-white dark:bg-schmersal-gray-darker divide-y divide-gray-200 dark:divide-schmersal-gray-dark">
                {partesOrdenadas.length === 0 ? (
                  <tr>
                    <td
                      colSpan={colSpan}
                      className="px-4 py-8 text-center text-sm text-gray-500 dark:text-white"
                    >
                      {t('apreciacaoTabelas.partes.vazio')}
                    </td>
                  </tr>
                ) : (
                  partesOrdenadas.map((dadosParte) => (
                    <SortableRow
                      key={dadosParte.parte}
                      dadosParte={dadosParte}
                      modoEdicao={modoEdicao}
                      onEditar={onEditar}
                      onCriarTemplate={onCriarTemplate}
                      onDuplicar={onDuplicar}
                      onDeletar={onDeletar}
                      onDetalharIA={onDetalharIA}
                      onBaixarImagens={onBaixarImagens}
                    />
                  ))
                )}
              </tbody>
            </SortableContext>
          </table>

          {/* Drag overlay — rendered in a portal with correct table context */}
          <DragOverlay>
            {activeItem ? (
              <table
                className="min-w-full"
                style={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'separate' }}
              >
                <tbody>
                  <OverlayRow dadosParte={activeItem} modoEdicao={modoEdicao} />
                </tbody>
              </table>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Resumo */}
      {partesOrdenadas.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-schmersal-gray-dark">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5 text-sm">
            <div>
              <span className="text-gray-500 dark:text-schmersal-gray-light">{t('apreciacaoTabelas.partes.resumo.totalPartes')}</span>
              <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                {partesOrdenadas.length}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-schmersal-gray-light">{t('apreciacaoTabelas.partes.resumo.prontas')}</span>
              <span className="ml-2 font-semibold text-green-600 dark:text-green-400">
                {partesOrdenadas.filter((p) => p.pronta).length}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-schmersal-gray-light">{t('apreciacaoTabelas.partes.resumo.naoProntas')}</span>
              <span className="ml-2 font-semibold text-orange-600 dark:text-orange-400">
                {partesOrdenadas.filter((p) => !p.pronta).length}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-schmersal-gray-light">{t('apreciacaoTabelas.partes.resumo.conformes')}</span>
              <span className="ml-2 font-semibold text-green-600 dark:text-green-400">
                {partesOrdenadas.filter((p) => p.conformeAtual).length}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-schmersal-gray-light">{t('apreciacaoTabelas.partes.resumo.totalRiscos')}</span>
              <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                {riscos.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
